import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../assets/web-app-manifest-512x512.png';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const result = await login(formData.email, formData.password);
            
            if (result.success) {
                toast.success('Login başarılı!');
                navigate('/dashboard');
            } else {
                toast.error(result.error || 'Giriş başarısız');
            }
        } catch (error) {
            console.error('Login error:', error);
            toast.error('Bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] flex flex-col md:flex-row">
            {/* Left side - Brand and information */}
            <div className="w-full md:w-1/2 bg-black p-8 md:p-16 flex flex-col justify-between relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10" 
                    style={{
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg width="20" height="20" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M1 0h2v2H1V0zm4 0h2v2H5V0zm4 0h2v2H9V0zm4 0h2v2h-2V0zm4 0h2v2h-2V0zM1 4h2v2H1V4zm4 0h2v2H5V4zm4 0h2v2H9V4zm4 0h2v2h-2V4zm4 0h2v2h-2V4zM1 8h2v2H1V8zm4 0h2v2H5V8zm4 0h2v2H9V8zm4 0h2v2h-2V8zm4 0h2v2h-2V8zm-12 4h2v2H1v-2zm4 0h2v2H5v-2zm4 0h2v2H9v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-12 4h2v2H1v-2zm4 0h2v2H5v-2zm4 0h2v2H9v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z" fill="%23FF3366" fill-opacity="0.4" fill-rule="evenodd"/%3E%3C/svg%3E")',
                        backgroundSize: '20px 20px'
                    }}
                ></div>
                
                {/* Decorative elements */}
                <div className="absolute top-40 left-20 w-64 h-64 rounded-full bg-gradient-to-r from-[#FF3366]/20 to-[#FF6B4D]/20 filter blur-3xl"></div>
                <div className="absolute bottom-40 right-20 w-80 h-80 rounded-full bg-gradient-to-r from-[#FF6B4D]/20 to-[#FF3366]/20 filter blur-3xl"></div>
                
                {/* Logo and brand */}
                <div className="relative z-10">
                    <div className="flex items-center mb-8">
                        <div className="relative">
                            <div className="h-12 w-12 z-10 relative flex items-center justify-center">
                                <img 
                                    src={Logo}  
                                    alt="UI Admin Logo" 
                                    className="h-12 w-auto"
                                />
                            </div>
                            <div className="absolute inset-0 bg-[#FF3366]/30 filter blur-md rounded-full z-0"></div>
                        </div>
                        <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] ml-3">
                            UI Admin
                        </h1>
                    </div>
                    
                    <h2 className="text-white text-4xl font-bold mb-6">Welcome back</h2>
                    <p className="text-gray-400 text-lg mb-8 max-w-md">
                        Access your admin dashboard to manage content, users, and tasks for your platform.
                    </p>
                </div>
                
                {/* Features */}
                <div className="relative z-10 space-y-6 mt-auto">
                    <h3 className="text-white text-xl font-semibold mb-4">Admin Dashboard Features</h3>
                    
                    <div className="flex items-start space-x-4">
                        <div className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] p-2 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-white font-semibold">Task Management</h4>
                            <p className="text-gray-400">Create, assign, and track tasks across your team.</p>
                        </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                        <div className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] p-2 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v10m2 4h-4M4 12h16M4 18h4" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-white font-semibold">Content Publishing</h4>
                            <p className="text-gray-400">Manage and publish content to your platform.</p>
                        </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                        <div className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] p-2 rounded-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <div>
                            <h4 className="text-white font-semibold">User Administration</h4>
                            <p className="text-gray-400">Manage user accounts, roles, and permissions.</p>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Right side - Login form */}
            <div className="w-full md:w-1/2 flex items-center justify-center p-8 md:p-16">
                <div className="w-full max-w-md">
                    <div className="bg-[#111111] border border-[#FF3366]/20 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-8">
                        <h2 className="text-2xl font-bold text-white mb-6">Sign in to your account</h2>
                        
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                        </svg>
                                    </div>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="block w-full pl-10 pr-3 py-3 border border-[#FF3366]/20 bg-[#0A0A0A] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#FF3366]/50 focus:border-transparent"
                                        placeholder="Enter your email address"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                                    Password
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        autoComplete="current-password"
                                        required
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        className="block w-full pl-10 pr-12 py-3 border border-[#FF3366]/20 bg-[#0A0A0A] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#FF3366]/50 focus:border-transparent"
                                        placeholder="Enter your password"
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                        <button
                                            type="button"
                                            className="text-gray-500 hover:text-gray-300 focus:outline-none"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeSlashIcon className="h-5 w-5" />
                                            ) : (
                                                <EyeIcon className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] hover:from-[#FF4177] hover:to-[#FF7A5E] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF3366] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            >
                                {loading ? (
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : null}
                                {loading ? 'Signing in...' : 'Sign in'}
                            </button>
                        </form>


                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login; 